import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import './i18n/i18n'; // Initialize i18n system

// Enhanced query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 60000,
      gcTime: 300000,
    },
  },
});

// Enhanced loader removal with error handling
const removeLoader = () => {
  try {
    const loader = document.querySelector('.initial-loader');
    if (loader) {
      loader.classList.add('fade-out');
      setTimeout(() => {
        if (loader.parentNode) {
          loader.remove();
        }
      }, 300);
    }
  } catch (error) {
    console.warn('Could not remove initial loader:', error);
  }
};

// Add fade-out animation CSS
const addFadeOutStyle = () => {
  const style = document.createElement('style');
  style.textContent = `
    .fade-out {
      opacity: 0;
      transition: opacity 0.3s ease-out;
    }
  `;
  document.head.appendChild(style);
};

// Enhanced application initialization
const initApp = async () => {
  try {
    console.log('🚀 Initializing Propmatch application...');
    
    addFadeOutStyle();
    
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('Root element not found');
    }
    
    const root = ReactDOM.createRoot(rootElement);
    
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <App />
          </HelmetProvider>
        </QueryClientProvider>
      </React.StrictMode>
    );
    
    // Remove loader after successful render
    setTimeout(removeLoader, 100);
    console.log('✅ Application initialized successfully');
    
  } catch (error) {
    console.error('❌ Failed to initialize the application:', error);
    
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; text-align: center; color: white; background: #0d0d0d;">
          <h2 style="margin-bottom: 16px;">Error al inicializar la aplicación</h2>
          <p style="margin-bottom: 20px; color: rgba(255,255,255,0.7);">
            Lo sentimos, ocurrió un error al inicializar la aplicación.
          </p>
          <div style="background: rgba(255,107,107,0.1); border: 1px solid #ff6b6b; padding: 12px; border-radius: 4px; margin-bottom: 20px; font-family: monospace; font-size: 14px; word-break: break-word;">
            ${error instanceof Error ? error.message : 'Error desconocido'}
          </div>
          <button 
            onclick="window.location.reload()" 
            style="padding: 10px 20px; background: #0050cd; border: none; color: white; border-radius: 4px; cursor: pointer; font-size: 16px;"
          >
            Reintentar
          </button>
        </div>
      `;
    }
  }
};

// Start application with comprehensive error handling
initApp().catch(error => {
  console.error('💥 Critical error during application initialization:', error);
  
  document.body.innerHTML = `
    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; text-align: center; color: white; background: #0d0d0d;">
      <h2 style="margin-bottom: 16px;">Error crítico</h2>
      <p style="margin-bottom: 20px; color: rgba(255,255,255,0.7);">
        No se pudo inicializar la aplicación debido a un error crítico.
      </p>
      <div style="background: rgba(255,107,107,0.1); border: 1px solid #ff6b6b; padding: 12px; border-radius: 4px; margin-bottom: 20px; font-family: monospace; font-size: 14px; word-break: break-word; max-width: 600px;">
        ${error instanceof Error ? error.message : 'Error desconocido'}
      </div>
      <button 
        onclick="window.location.reload()" 
        style="padding: 10px 20px; background: #0050cd; border: none; color: white; border-radius: 4px; cursor: pointer; font-size: 16px;"
      >
        Reintentar
      </button>
    </div>
  `;
});
